if (!customElements.get('mask-sizing-guide')) {
  class MaskSizingGuide extends HTMLElement {
    constructor() {
      super();
      this.rules = MaskSizingGuide.parseJson(this.querySelector('[data-mask-sizing-rules]').textContent);
      this.variants = MaskSizingGuide.parseJson(this.querySelector('[data-mask-sizing-variants]').textContent) || [];
      this.images = MaskSizingGuide.parseJson(this.dataset.images) || {};
      this.copy = MaskSizingGuide.parseJson(this.querySelector('[data-mask-sizing-copy]').textContent) || {};
      this.product = this.rules && this.rules.products ? this.rules.products[this.dataset.productHandle] : null;
      if (!this.product) return;

      this.units = this.copy.units || '';
      this.measurements = {};
      Object.keys(this.rules.measurements).forEach((key) => {
        this.measurements[key] = Object.assign({}, this.rules.measurements[key], this.copy[key] || {});
      });
      this.steps = (this.rules.step_order || []).filter((key) => this.product.sizes.some((size) => size.ranges[key]));
      this.sizes = this.product.sizes.filter((size) => {
        const variant = this.variantFor(size);
        return variant && variant.available;
      });

      this.section = this.closest('.shopify-section');
      this.modal = this.closest('modal-dialog');
      this.panels = {
        intro: this.querySelector('[data-mask-sizing-panel="intro"]'),
        step: this.querySelector('[data-mask-sizing-panel="step"]'),
        result: this.querySelector('[data-mask-sizing-panel="result"]'),
        nomatch: this.querySelector('[data-mask-sizing-panel="nomatch"]')
      };
      this.input = this.querySelector('[data-mask-sizing="input"]');
      this.error = this.querySelector('[data-mask-sizing-error]');
      this.live = this.querySelector('[data-mask-sizing-live]');
      this.nextBtn = this.querySelector('[data-mask-sizing="next"]');
      this.backBtn = this.querySelector('[data-mask-sizing="back"]');
      this.text = this.panels.step.dataset;

      this.values = {};
      this.stepIndex = 0;
      this.isOpen = false;

      this.renderCalcText();
      this.addEventListener('click', this.handleClick.bind(this));
      this.panels.step.addEventListener('submit', this.handleSubmit.bind(this));
      this.input.addEventListener('input', this.clearError.bind(this));

      if (this.modal) {
        this.observer = new MutationObserver(this.handleModalToggle.bind(this));
        this.observer.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    variantFor(size) {
      return this.variants.find((v) => v.value_id === size.value_id);
    }

    static parseJson(raw) {
      try {
        return JSON.parse(raw);
      } catch (e) {
        return null;
      }
    }

    static parseNumber(raw) {
      const cleaned = String(raw).trim().replace(',', '.');
      if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return NaN;
      return Math.round(parseFloat(cleaned) * 100) / 100;
    }

    static formatNumber(num) {
      return String(Math.round(num * 100) / 100);
    }

    static fill(template, vars) {
      return Object.keys(vars).reduce((str, key) => str.split(`[${key}]`).join(vars[key]), template || '');
    }

    handleModalToggle() {
      const isOpen = this.modal.hasAttribute('open');
      if (isOpen === this.isOpen) return;
      this.isOpen = isOpen;
      if (!isOpen) this.reset();
    }

    handleClick(evt) {
      const target = evt.target.closest('[data-mask-sizing]');
      if (!target) return;
      const action = target.dataset.maskSizing;

      if (action === 'cta-calculator') this.start();
      else if (action === 'back') this.back();
      else if (action === 'step-up') this.nudge(0.1);
      else if (action === 'step-down') this.nudge(-0.1);
      else if (action === 'retake' || action === 'retake-nomatch') this.start();
      else if (action === 'result-atc') this.addToCart();
      else if (action === 'share') this.copyLink();
    }

    handleSubmit(evt) {
      evt.preventDefault();
      const key = this.steps[this.stepIndex];
      const measurement = this.measurements[key];
      const value = MaskSizingGuide.parseNumber(this.input.value);

      if (Number.isNaN(value)) {
        this.showError(this.text.errorNumber);
        return;
      }
      if (value < measurement.min || value > measurement.max) {
        this.showError(MaskSizingGuide.fill(this.text.errorRange, { min: measurement.min, max: measurement.max, units: this.units }));
        return;
      }

      this.values[key] = value;

      if (this.stepIndex < this.steps.length - 1) {
        this.stepIndex += 1;
        this.renderStep();
      } else {
        this.finish();
      }
    }

    renderCalcText() {
      const el = this.querySelector('[data-mask-sizing-calc-text]');
      if (!el) return;
      const template = this.steps.length === 1 && el.dataset.templateSingle ? el.dataset.templateSingle : el.dataset.template;
      el.textContent = MaskSizingGuide.fill(template, { count: this.steps.length });
    }

    showPanel(name) {
      Object.keys(this.panels).forEach((key) => {
        this.panels[key].hidden = key !== name;
      });
      const scroller = this.closest('.modal__window');
      if (scroller) scroller.scrollTop = 0;
    }

    start() {
      this.values = {};
      this.stepIndex = 0;
      this.renderStep();
      this.showPanel('step');
      this.input.focus({ preventScroll: true });
    }

    reset() {
      this.values = {};
      this.stepIndex = 0;
      this.input.value = '';
      this.clearError();
      const copied = this.querySelector('[data-mask-sizing-copied]');
      if (copied) copied.hidden = true;
      this.showPanel('intro');
    }

    back() {
      if (this.stepIndex === 0) {
        this.reset();
        return;
      }
      this.stepIndex -= 1;
      this.renderStep();
      this.input.focus({ preventScroll: true });
    }

    renderStep() {
      const key = this.steps[this.stepIndex];
      const measurement = this.measurements[key];
      const total = this.steps.length;
      const isLast = this.stepIndex === total - 1;
      const stepCount = MaskSizingGuide.fill(this.text.stepCount, { current: this.stepIndex + 1, total });

      this.querySelector('[data-mask-sizing-step-title]').textContent = `${this.stepIndex + 1}. ${measurement.title}`;
      this.querySelector('[data-mask-sizing-step-text]').textContent = measurement.description;
      this.querySelector('[data-mask-sizing-step-label]').textContent = measurement.label;
      this.querySelector('[data-mask-sizing-step-count]').textContent = stepCount;

      const img = this.querySelector('[data-mask-sizing-step-image]');
      img.src = this.images[key] || '';
      img.alt = measurement.title;

      this.panels.step.dataset.step = key;
      this.nextBtn.dataset.step = key;
      this.input.placeholder = measurement.placeholder;
      this.input.value = key in this.values ? MaskSizingGuide.formatNumber(this.values[key]) : '';
      this.clearError();

      this.querySelector('[data-mask-sizing-next-label]').textContent = isLast ? this.nextBtn.dataset.submitLabel : this.nextBtn.dataset.nextLabel;
      this.nextBtn.classList.toggle('mask-sizing__next--full', isLast);
      this.backBtn.hidden = isLast;

      this.renderProgress(this.panels.step, this.stepIndex, false);
      this.announce(`${stepCount}: ${measurement.title}`);
    }

    renderProgress(panel, activeIndex, done) {
      const list = panel.querySelector('[data-mask-sizing-progress]');
      const check = list.querySelector('[data-mask-sizing-progress-check]');
      list.querySelectorAll('.mask-sizing__dot:not([data-mask-sizing-progress-check]), .mask-sizing__bar').forEach((el) => el.remove());

      this.steps.forEach((key, i) => {
        const dot = document.createElement('li');
        dot.className = 'mask-sizing__dot';
        if (done || i < activeIndex) dot.classList.add('is-done');
        else if (i === activeIndex) dot.classList.add('is-active');
        const bar = document.createElement('li');
        bar.className = 'mask-sizing__bar';
        if (done || i < activeIndex) bar.classList.add('is-done');
        list.insertBefore(dot, check);
        list.insertBefore(bar, check);
      });
      check.classList.toggle('is-done', done);
    }

    nudge(delta) {
      const measurement = this.measurements[this.steps[this.stepIndex]];
      let value = MaskSizingGuide.parseNumber(this.input.value);
      value = Number.isNaN(value) ? measurement.min : value + delta;
      value = Math.min(measurement.max, Math.max(measurement.min, value));
      this.input.value = MaskSizingGuide.formatNumber(value);
      this.clearError();
      this.input.focus({ preventScroll: true });
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
      this.input.setAttribute('aria-invalid', 'true');
      this.input.focus({ preventScroll: true });
    }

    clearError() {
      this.error.hidden = true;
      this.error.textContent = '';
      this.input.removeAttribute('aria-invalid');
    }

    fits(size) {
      return this.steps.every((key) => {
        const range = size.ranges[key];
        if (!range) return true;
        const value = this.values[key];
        if (range[0] !== null && value < range[0]) return false;
        if (range[1] !== null && value > range[1]) return false;
        return true;
      });
    }

    recommend() {
      const fitting = this.sizes.filter((size) => this.fits(size));
      if (!fitting.length) return null;
      return this.product.overlap === 'smallest' ? fitting[0] : fitting[fitting.length - 1];
    }

    finish() {
      this.recommended = this.recommend();
      const panelName = this.recommended ? 'result' : 'nomatch';
      const panel = this.panels[panelName];

      if (this.recommended) {
        const variant = this.variantFor(this.recommended);
        this.querySelector('[data-mask-sizing-result-size]').textContent = variant ? variant.value : this.recommended.value;
        this.querySelector('[data-mask-sizing="result-atc"]').dataset.recommended = this.recommended.value;
      }
      this.renderMeasurements(panel.querySelector('[data-mask-sizing-measurements]'));
      this.renderProgress(panel, this.steps.length, true);
      this.showPanel(panelName);

      const heading = panel.querySelector('h2');
      this.announce(this.recommended ? `${heading.textContent}: ${this.querySelector('[data-mask-sizing-result-size]').textContent}` : heading.textContent);
      heading.focus({ preventScroll: true });
    }

    renderMeasurements(list) {
      list.textContent = '';
      this.steps.forEach((key) => {
        const item = document.createElement('div');
        item.className = 'mask-sizing__measurement';
        const dt = document.createElement('dt');
        dt.textContent = this.measurements[key].short_label;
        const dd = document.createElement('dd');
        dd.textContent = `${MaskSizingGuide.formatNumber(this.values[key])} ${this.units}`;
        item.append(dt, dd);
        list.append(item);
      });
    }

    selectRecommendedSize() {
      if (!this.section || !this.recommended) return false;
      const picker = this.section.querySelector('variant-picker');
      if (!picker) return false;
      const selector = picker.querySelectorAll('.option-selector')[Number(this.dataset.optionIndex)];
      if (!selector) return false;
      const variant = this.variantFor(this.recommended);
      if (!variant) return false;

      const customSelect = selector.querySelector('custom-select');
      if (customSelect) {
        const option = Array.from(customSelect.querySelectorAll('.custom-select__option')).find((li) => li.dataset.valueId === String(variant.value_id));
        if (!option || typeof customSelect.selectOption !== 'function') return false;
        customSelect.selectOption(option);
        return true;
      }

      const radio = selector.querySelectorAll('input.js-option')[variant.index];
      if (!radio) return false;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    addToCart() {
      const selected = this.selectRecommendedSize();

      if (this.modal) this.modal.close();
      if (!selected) return;

      const submit = this.section.querySelector('form[data-product-form] [type="submit"]');
      if (submit && !submit.disabled) submit.click();
    }

    copyLink() {
      const copied = this.querySelector('[data-mask-sizing-copied]');
      const show = () => { if (copied) copied.hidden = false; };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(window.location.href.split('#')[0]).then(show, show);
      } else {
        show();
      }
    }

    announce(text) {
      if (this.live) this.live.textContent = text;
    }
  }

  customElements.define('mask-sizing-guide', MaskSizingGuide);
}
